import React, { useEffect } from "react";
import { FormControlLabel, FormGroup, FormHelperText } from "@mui/material";
import { Checkbox, TextField } from "@outpave/ds";
import { Stack } from "components/layout/reusableLayouts";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useTranslation } from "react-i18next";
import useGetLoggedInUser from "hooks/useGetLoggedInUser";
import { OnboardingProps } from "pages/onboarding/OnboardCompany";
import { primaryAuthorizedPersonValidation } from "validations/primaryAuthorizedPerson";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";
import useAddUpdateUser, {
  UsersQueryData,
  userFromNode,
} from "hooks/useAddUpdateUser";
import { GET_USERS } from "graphql/user";
import { useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import UserForm from "./UserForm";
import Role from "enums/Role";
import UserStatus from "enums/UserStatus";
import { useFormikHelpers, getFirstErrorMessage } from "hooks/useFormikHelpers";
import PatriotActNotice from "components/common/PatriotActNotice";
import StepHeader from "./StepHeader";
import StepFooter from "./StepFooter";

const PrimaryAuthorizedPerson: React.FC<OnboardingProps> = (props) => {
  const { t } = useTranslation();
  const { user: loggedInUser } = useGetLoggedInUser();
  const {
    onboardingInfo,
    handleNext,
    handleBack,
    ownersRequired,
    setOwnersRequired,
  } = props;
  const [loading, setLoading] = React.useState(false);

  const authUser = (onboardingInfo?.userProfiles ?? []).find(
    (profile) => profile.email === loggedInUser?.email,
  );

  const {
    formik: authUserProfile,
    loading: userProfileLoading,
    setUser,
  } = useAddUpdateUser({
    validationSchema: primaryAuthorizedPersonValidation,
    initialValues: {
      identifyingType: "SSN",
      issuingCountry: "US",
      role: Role.Admin,
      status: UserStatus.INACTIVE,
      authorizationConfirmed: false,
    },
    onCompleted: (values) => {
      handleNext({
        userProfiles: values,
      });
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const { validateAndTouch: validateAuthUser } =
    useFormikHelpers(authUserProfile);

  const { data: usersData, loading: userLoading } = useQuery<UsersQueryData>(
    GET_USERS,
    {
      variables: {
        email: loggedInUser?.email,
      },
      fetchPolicy: "network-only",
      skip: Boolean(authUser),
    },
  );

  useEffect(() => {
    const node = usersData?.users?.edges?.[0]?.node;
    if (node) {
      const existing = userFromNode(node);
      setUser({
        ...existing,
        location: {
          id: existing.location?.id || onboardingInfo?.addressProfile?.id || "",
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersData]);

  useEffect(() => {
    if (authUser) {
      setUser({
        ...authUser,
        location: {
          id: authUser.location?.id || onboardingInfo?.addressProfile?.id || "",
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser]);

  useEffect(() => {
    setLoading(userLoading || userProfileLoading);
  }, [userLoading, userProfileLoading]);

  useEffect(() => {
    if (authUserProfile.values.isOwner) {
      setOwnersRequired?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUserProfile.values.isOwner]);

  useEffect(() => {
    if (onboardingInfo?.addressProfile?.id) {
      authUserProfile.setFieldValue(
        "location.id",
        onboardingInfo.addressProfile.id,
        false,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingInfo?.addressProfile?.id]);

  async function handleSubmit() {
    const isAuthUserValid = await validateAuthUser();

    if (!isAuthUserValid) {
      const firstError = getFirstErrorMessage(authUserProfile.errors);
      toast.error(
        firstError ? t(firstError) : t("Please complete the required fields"),
      );
      return;
    }

    await authUserProfile.submitForm();
  }

  const handleBackClick = () => {
    handleBack({
      userProfiles: authUserProfile.values,
    });
  };

  return (
    <>
      <StepHeader
        title={t("Authorization")}
        subtitle={t("AuthorizationDescription")}
      >
        <PatriotActNotice />
      </StepHeader>

      <Stack
        component="form"
        gap="var(--space-5)"
        sx={{ marginTop: "var(--space-7)" }}
      >
        <TextField
          fullWidth
          readOnly
          label={t("Email")}
          name="email"
          value={authUserProfile.values.email || loggedInUser?.email || ""}
          helperText={t("AuthorizedEmailHelper")}
          trailing={<LockOutlinedIcon fontSize="small" />}
          onClick={() => toast.info(t("AuthorizedEmailLockedToast"))}
        />
        <UserForm formik={authUserProfile} showEmailField={false} />
        <FormGroup sx={{ marginTop: "var(--space-3)" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={ownersRequired ?? false}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setOwnersRequired?.(checked);
                  if (!checked) {
                    authUserProfile.setFieldValue("isOwner", false);
                  }
                }}
              />
            }
            label={t("AtLeastOneOwnerLabel")}
          />
          {ownersRequired && (
            <FormControlLabel
              control={
                <Checkbox
                  name="isOwner"
                  checked={authUserProfile.values.isOwner}
                  onChange={authUserProfile.handleChange}
                />
              }
              label={t("IAmAlsoBeneficialOwnerLabel")}
            />
          )}
          <FormControlLabel
            control={
              <Checkbox
                name="authorizationConfirmed"
                checked={authUserProfile.values.authorizationConfirmed}
                onChange={authUserProfile.handleChange}
              />
            }
            label={t("I am authorized to act on behalf of my business")}
          />
          {authUserProfile.touched.authorizationConfirmed &&
            typeof authUserProfile.errors.authorizationConfirmed ===
              "string" && (
              <FormHelperText error>
                {authUserProfile.errors.authorizationConfirmed}
              </FormHelperText>
            )}
        </FormGroup>
      </Stack>

      <StepFooter
        primaryLabel={t("Next")}
        onPrimary={handleSubmit}
        primaryDisabled={!authUserProfile.values.authorizationConfirmed}
        secondaryLabel={t("Previous")}
        onSecondary={handleBackClick}
      />
      <LoadingWithoutModal open={loading} />
    </>
  );
};

export default PrimaryAuthorizedPerson;
